import { Component, Input, signal, inject, OnInit, OnChanges, SimpleChanges, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ProjectService } from '../../../../core/services/project.service';
import { Project, StepPermissions, Workflow, CustomStatus, MetaStatus } from '../../../../core/models/models';
import { WorkflowService } from '../../../../core/services/workflow.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';
import { FatService, FatDto } from '../../../../core/services/fat.service';
import { CustomStatusService } from '../../../../core/services/custom-status.service';

export const dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const availabilityDate = control.get('availabilityDate')?.value;
  const deliveryDate = control.get('deliveryDate')?.value;

  if (availabilityDate && deliveryDate && availabilityDate > deliveryDate) {
    return { dateRangeInvalid: true };
  }
  return null;
};

@Component({
  selector: 'app-project-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, ConfirmationDialogComponent],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss'
})
export class ProjectDetailsComponent implements OnChanges, OnInit {
  private fb = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private workflowService = inject(WorkflowService);
  private fatService = inject(FatService);
  private customStatusService = inject(CustomStatusService);

  @Input({ required: true }) project!: Project;
  @Input() isReadOnly = false;

  saved = output<void>();
  error = output<string>();

  isSaving = signal(false);
  successMessage = signal('');
  localErrorMessage = signal('');
  currentWorkflow = signal<Workflow | null>(null);
  workflowPermissions = signal<StepPermissions | null>(null);
  isLocked = signal(false);
  isWorkflowConfirmDialogOpen = signal(false);
  isErrorDialogOpen = signal(false);
  pendingWorkflowAction = signal<'validate' | 'reject' | null>(null);
  allStatuses = signal<CustomStatus[]>([]);
  projectFats = signal<FatDto[]>([]);

  projectForm = this.fb.group({
    name: ['', Validators.required],
    deliveryAddress: [''],
    city: [''],
    zipCode: [''],
    availabilityDate: ['', Validators.required],
    deliveryDate: ['', Validators.required],
    affaireId: [null as number | null],
    workflowId: [null as number | null]
  }, { validators: dateRangeValidator });

  ngOnInit(): void {
    this.loadCurrentWorkflowDetails();
    this.loadAllStatuses();
    this.loadProjectFats();

    this.projectForm.get('workflowId')?.valueChanges.subscribe(() => {
      this.loadCurrentWorkflowDetails();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      this.projectForm.patchValue({
        name: this.project.name,
        deliveryAddress: this.project.deliveryAddress,
        city: this.project.city,
        zipCode: this.project.zipCode,
        availabilityDate: this.project.availabilityDate,
        deliveryDate: this.project.deliveryDate,
        affaireId: this.project.affaireId,
        workflowId: this.project.workflowId ?? null
      });

      this.isLocked.set(this.project.metaStatus !== 'DRAFT');
      if (this.isReadOnly) {
        this.projectForm.disable({ emitEvent: false });
      } else if (this.isLocked()) {
        // Locked projects can only update workflow assignment
        this.projectForm.disable({ emitEvent: false });
        this.projectForm.get('workflowId')?.enable({ emitEvent: false });
      } else {
        this.projectForm.enable({ emitEvent: false });
      }

      this.loadCurrentWorkflowDetails();
      this.loadWorkflowPermissions();
      this.loadProjectFats();
    }
  }

  getStatusLabel(status: string): string {
    return this.project.statusName || status;
  }

  onSubmit(): void {
    if (this.projectForm.valid && this.project.id) {
      this.isSaving.set(true);
      this.successMessage.set('');

      this.projectService.updateProject(this.project.id, this.projectForm.value as any).subscribe({
        next: () => {
          this.successMessage.set('Projet mis à jour avec succès');
          this.isSaving.set(false);
          this.saved.emit();
          this.loadCurrentWorkflowDetails();
          this.loadWorkflowPermissions();
          setTimeout(() => this.successMessage.set(''), 3000);
        },
        error: (err) => {
          this.error.emit(err?.error?.message || err?.error || 'Erreur lors de la mise à jour du projet');
          this.isSaving.set(false);
        }
      });
    }
  }

  loadWorkflowPermissions(): void {
    if (!this.project?.id) {
      return;
    }
    this.projectService.getWorkflowPermissions(this.project.id).subscribe({
      next: permissions => this.workflowPermissions.set(permissions),
      error: () => this.workflowPermissions.set(null)
    });
  }

  requestValidateWorkflow(): void {
    if (!this.workflowPermissions()?.canValidate) {
      const message = "Vous n'avez pas la permission de valider cette étape.";
      this.localErrorMessage.set(message);
      this.error.emit(message);
      return;
    }

    // Pré-vérification : au moins 2 sous-traitants pour l'étape 1
    if (this.project.currentStepOrder === 1 && (!this.project.sousTraitantIds || this.project.sousTraitantIds.length < 2)) {
      const message = "Au moins deux sous-traitants doivent être assignés pour valider la première étape du projet.";
      this.localErrorMessage.set(message);
      this.error.emit(message);
      this.isErrorDialogOpen.set(true);
      return;
    }

    // NOUVELLE VÉRIFICATION : Le projet ne peut pas passer en TERMINE si la FAT n'est pas TERMINE
    const workflow = this.currentWorkflow();
    if (workflow && workflow.steps) {
      const currentStep = workflow.steps.find(s => s.stepOrder === this.project.currentStepOrder);
      if (currentStep) {
        const nextStep = workflow.steps
          .filter(s => (s.stepOrder || 0) > (currentStep.stepOrder || 0))
          .sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0))[0];

        const targetStatusId = nextStep ? nextStep.initialStatusId : currentStep.finalStatusId;
        const targetStatus = this.allStatuses().find(s => s.id === targetStatusId);

        if (targetStatus?.metaStatus === MetaStatus.TERMINE) {
          const fatTermine = this.projectFats().some(f => f.statusMeta === MetaStatus.TERMINE);
          if (!fatTermine) {
            const message = "Le projet ne peut pas être terminé tant que la FAT n'est pas dans un statut terminé.";
            this.localErrorMessage.set(message);
            this.error.emit(message);
            this.isErrorDialogOpen.set(true);
            return;
          }
        }
      }
    }

    this.pendingWorkflowAction.set('validate');
    this.isWorkflowConfirmDialogOpen.set(true);
  }

  requestRejectWorkflow(): void {
    if (!this.workflowPermissions()?.canReject) {
      const message = "Vous n'avez pas la permission de rejeter cette étape.";
      this.localErrorMessage.set(message);
      this.error.emit(message);
      return;
    }
    this.pendingWorkflowAction.set('reject');
    this.isWorkflowConfirmDialogOpen.set(true);
  }



  confirmWorkflowAction(): void {
    const action = this.pendingWorkflowAction();
    if (!action) {
      return;
    }
    if (action === 'validate') {
      this.validateWorkflow();
    } else {
      this.rejectWorkflow();
    }
  }

  closeWorkflowConfirmDialog(): void {
    this.isWorkflowConfirmDialogOpen.set(false);
    this.pendingWorkflowAction.set(null);
  }

  closeErrorDialog(): void {
    this.isErrorDialogOpen.set(false);
    this.localErrorMessage.set('');
  }

  getWorkflowConfirmTitle(): string {
    const action = this.pendingWorkflowAction();
    if (action === 'validate') {
      return "Valider l'étape";
    }
    if (action === 'reject') {
      return "Rejeter l'étape";
    }
    return '';
  }

  getWorkflowConfirmMessage(): string {
    const action = this.pendingWorkflowAction();
    if (action === 'validate') {
      return "Voulez-vous confirmer la validation de cette étape ?";
    }
    if (action === 'reject') {
      return "Voulez-vous confirmer le rejet de cette étape ?";
    }
    return '';
  }

  getWorkflowConfirmLabel(): string {
    const action = this.pendingWorkflowAction();
    if (action === 'validate') {
      return 'Valider';
    }
    if (action === 'reject') {
      return 'Rejeter';
    }
    return '';
  }

  private validateWorkflow(): void {
    if (!this.project?.id) {
      return;
    }
    this.projectService.validateWorkflow(this.project.id).subscribe({
      next: () => {
        this.successMessage.set('Étape validée');
        this.localErrorMessage.set('');
        this.closeWorkflowConfirmDialog();
        this.saved.emit();
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        const message = err?.error?.message || err?.error || 'Validation de workflow impossible';
        this.localErrorMessage.set(message);
        this.error.emit(message);
        this.closeWorkflowConfirmDialog();
        this.isErrorDialogOpen.set(true);
      }
    });
  }

  private rejectWorkflow(): void {
    if (!this.project?.id) {
      return;
    }
    this.projectService.rejectWorkflow(this.project.id).subscribe({
      next: () => {
        this.successMessage.set('Étape rejetée');
        this.localErrorMessage.set('');
        this.closeWorkflowConfirmDialog();
        this.saved.emit();
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        const message = err?.error?.message || err?.error || 'Rejet de workflow impossible';
        this.localErrorMessage.set(message);
        this.error.emit(message);
        this.closeWorkflowConfirmDialog();
        this.isErrorDialogOpen.set(true);
      }
    });
  }



  getWorkflowActionsLabel(permissions: string[] | undefined): string {
    if (!permissions || permissions.length === 0) {
      return 'Aucune action';
    }
    const filtered = permissions.filter(p => p !== 'COMMENT');
    return filtered.length ? filtered.join(', ') : 'Aucune action';
  }

  isWorkflowCompleted(): boolean {
    const workflow = this.currentWorkflow();
    if (!workflow || !workflow.steps?.length) {
      return false;
    }
    const lastStep = workflow.steps.reduce((acc, step) => step.stepOrder > acc.stepOrder ? step : acc, workflow.steps[0]);
    if (this.project?.statusId && lastStep.finalStatusId) {
      return this.project.statusId === lastStep.finalStatusId;
    }
    if (this.project?.statusName && lastStep.finalStatusName) {
      return this.project.statusName === lastStep.finalStatusName;
    }
    return false;
  }

  isStepCompleted(step: any): boolean {
    if (this.isWorkflowCompleted()) {
      return true;
    }
    if (this.project.currentStepOrder && step.stepOrder) {
      return this.project.currentStepOrder > step.stepOrder;
    }
    return false;
  }

  private loadCurrentWorkflowDetails(): void {
    const workflowId = this.projectForm.get('workflowId')?.value;
    if (!workflowId) {
      this.currentWorkflow.set(null);
      return;
    }

    this.workflowService.getWorkflowById(workflowId).subscribe({
      next: workflow => this.currentWorkflow.set(workflow),
      error: () => this.error.emit('Impossible de charger le détail du workflow')
    });
  }

  private loadAllStatuses(): void {
    this.customStatusService.getAll().subscribe({
      next: statuses => this.allStatuses.set(statuses),
      error: () => console.error('Erreur lors du chargement des statuts')
    });
  }

  private loadProjectFats(): void {
    if (!this.project?.id) {
      this.projectFats.set([]);
      return;
    }
    this.fatService.getProjectFats(this.project.id).subscribe({
      next: fats => this.projectFats.set(fats),
      error: () => console.error('Erreur lors du chargement des FATs')
    });
  }
}
